import { useEffect, useMemo, useState } from "react";

import api, { downloadFile } from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import ActionButton from "../components/ActionButton";

type Customer = { id: number; name: string };
type Contract = { id: number; customer_id: number; name: string; contract_type: string };
type Invoice = {
  id: number;
  customer_id: number;
  contract_id: number | null;
  invoice_number: string | null;
  status: string;
  period_start: string;
  period_end: string;
  issue_date: string | null;
  due_date: string | null;
  paid_at: string | null;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  amount_paid_cents: number;
  balance_cents: number;
  currency: string;
};
type PaymentRow = {
  id: number;
  amount_cents: number;
  method: string;
  reference: string | null;
  received_at: string | null;
  note: string | null;
};
type LineItem = {
  id: number;
  kind: string;
  description: string;
  quantity_thousandths: number;
  unit_label: string;
  unit_rate_cents: number;
  total_cents: number;
};

const STATUS_PILL: Record<string, string> = {
  draft: "bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300",
  sent: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  paid: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  void: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

const fmt = (cents: number) =>
  `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
const fmtHours = (thousandths: number) =>
  `${(thousandths / 1000).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function PSAInvoices() {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [filterCustomer, setFilterCustomer] = useState<number | "">("");
  const [generating, setGenerating] = useState<{
    customer_id: number | "";
    contracts: Contract[];
    contract_id: number | "";
    period_start: string;
    period_end: string;
    customer_message: string;
  } | null>(null);
  const [openInvoice, setOpenInvoice] = useState<(Invoice & { line_items: LineItem[] }) | null>(null);
  const [payments, setPayments] = useState<PaymentRow[]>([]);
  const [payForm, setPayForm] = useState<{ amount: string; method: string; reference: string }>({
    amount: "",
    method: "ach",
    reference: "",
  });
  const [err, setErr] = useState("");
  const [working, setWorking] = useState(false);

  useEffect(() => {
    api.get("/api/v1/customers").then((r) => setCustomers(r.data || []));
  }, []);

  const load = async () => {
    setErr("");
    const params: Record<string, unknown> = {};
    if (filterCustomer !== "") params.customer_id = filterCustomer;
    try {
      const r = await api.get("/api/v1/psa/invoices", { params });
      setInvoices(r.data || []);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load invoices");
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterCustomer]);

  const openGenerate = async () => {
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
    const lastOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
    setGenerating({
      customer_id: "",
      contracts: [],
      contract_id: "",
      period_start: firstOfMonth,
      period_end: lastOfMonth,
      customer_message: "",
    });
  };

  const loadContractsForGenerate = async (customerId: number) => {
    const r = await api.get("/api/v1/psa/contracts", {
      params: { customer_id: customerId, status: "active" },
    });
    setGenerating((g) =>
      g
        ? {
            ...g,
            customer_id: customerId,
            contracts: r.data || [],
            contract_id: r.data?.[0]?.id ?? "",
          }
        : null,
    );
  };

  const generate = async () => {
    if (!generating || generating.customer_id === "") return;
    setErr("");
    setWorking(true);
    try {
      await api.post("/api/v1/psa/invoices", {
        customer_id: generating.customer_id,
        contract_id: generating.contract_id || undefined,
        period_start: generating.period_start,
        period_end: generating.period_end,
        customer_message: generating.customer_message || undefined,
      });
      setGenerating(null);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Generate failed");
    } finally {
      setWorking(false);
    }
  };

  const loadPayments = async (invoiceId: number) => {
    try {
      const r = await api.get(`/api/v1/psa/invoices/${invoiceId}/payments`);
      setPayments(r.data || []);
    } catch {
      setPayments([]);
    }
  };

  const openDetail = async (inv: Invoice) => {
    try {
      const r = await api.get(`/api/v1/psa/invoices/${inv.id}`);
      setOpenInvoice(r.data);
      setPayForm({ amount: "", method: "ach", reference: "" });
      loadPayments(inv.id);
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Failed to load invoice");
    }
  };

  const downloadPdf = async () => {
    if (!openInvoice) return;
    try {
      await downloadFile(
        `/api/v1/psa/invoices/${openInvoice.id}/pdf`,
        `invoice-${openInvoice.invoice_number || openInvoice.id}.pdf`,
      );
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "PDF download failed");
    }
  };

  const emailInvoice = async () => {
    if (!openInvoice) return;
    if (!window.confirm("Email this invoice PDF to the customer's billing address?")) return;
    setWorking(true);
    setErr("");
    try {
      const r = await api.post(`/api/v1/psa/invoices/${openInvoice.id}/send`, {});
      setOpenInvoice({ ...openInvoice, ...r.data });
      window.alert(`Sent to ${r.data.sent_to}`);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Send failed");
    } finally {
      setWorking(false);
    }
  };

  const recordPayment = async () => {
    if (!openInvoice) return;
    const cents = Math.round(parseFloat(payForm.amount || "0") * 100);
    if (!cents) {
      setErr("Enter a non-zero payment amount");
      return;
    }
    setWorking(true);
    setErr("");
    try {
      const r = await api.post(`/api/v1/psa/invoices/${openInvoice.id}/payments`, {
        amount_cents: cents,
        method: payForm.method,
        reference: payForm.reference || undefined,
      });
      setOpenInvoice({ ...openInvoice, ...r.data.invoice });
      setPayForm({ amount: "", method: payForm.method, reference: "" });
      loadPayments(openInvoice.id);
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Payment failed");
    } finally {
      setWorking(false);
    }
  };

  const finalize = async () => {
    if (!openInvoice) return;
    setWorking(true);
    try {
      const r = await api.post(`/api/v1/psa/invoices/${openInvoice.id}/finalize`, {
        tax_cents: 0,
      });
      setOpenInvoice({ ...openInvoice, ...r.data });
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Finalize failed");
    } finally {
      setWorking(false);
    }
  };

  const markPaid = async () => {
    if (!openInvoice) return;
    setWorking(true);
    try {
      const r = await api.post(`/api/v1/psa/invoices/${openInvoice.id}/mark-paid`, {});
      setOpenInvoice({ ...openInvoice, ...r.data });
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Mark paid failed");
    } finally {
      setWorking(false);
    }
  };

  const voidInv = async () => {
    if (!openInvoice) return;
    if (!window.confirm(`Void ${openInvoice.invoice_number || "this draft"}?`)) return;
    setWorking(true);
    try {
      const r = await api.post(`/api/v1/psa/invoices/${openInvoice.id}/void`);
      setOpenInvoice({ ...openInvoice, ...r.data });
      load();
    } catch (e: unknown) {
      const ex = e as { response?: { data?: { detail?: string } } };
      setErr(ex.response?.data?.detail || "Void failed");
    } finally {
      setWorking(false);
    }
  };

  const cName = (id: number) => customers.find((c) => c.id === id)?.name || `customer ${id}`;
  const invSort = useSort("invoice_number");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Invoices",
    useMemo(
      () =>
        invoices.map((i: any) => ({
          number: i.invoice_number,
          customer: i.customer_name ?? null,
          status: i.status,
          period_start: i.period_start ?? null,
          period_end: i.period_end ?? null,
          total: (i.total_cents ?? 0) / 100,
          due: i.due_date ?? null,
        })),
      [invoices],
    ),
    undefined,
  );
  const liSort = useSort("description");

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">PSA — Invoices</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Generate draft invoices that roll up billable time entries against an active
            contract. Finalize to lock the invoice number + line items.
          </p>
        </div>
        <ActionButton onAction={() => openGenerate()} variant="raw" className="px-4 py-2 bg-brand text-white rounded-lg text-sm font-medium hover:opacity-90" pendingLabel="Generating…">
          Generate invoice
        </ActionButton>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-end gap-3">
        <label className="text-sm">
          <span className="block text-gray-600 dark:text-gray-300 mb-1">Filter by customer</span>
          <select
            value={filterCustomer}
            onChange={(e) =>
              setFilterCustomer(e.target.value === "" ? "" : Number(e.target.value))
            }
            className="border dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-900 dark:text-gray-100"
          >
            <option value="">All customers</option>
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {err && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900 text-gray-700 dark:text-gray-300">
            <tr>
              <SortableTh label="Number" sortKey="invoice_number" sort={invSort.sort} onSort={invSort.toggle} />
              <SortableTh label="Customer" sortKey="customer_id" sort={invSort.sort} onSort={invSort.toggle} />
              <SortableTh label="Period" sortKey="period_start" sort={invSort.sort} onSort={invSort.toggle} />
              <SortableTh label="Status" sortKey="status" sort={invSort.sort} onSort={invSort.toggle} />
              <SortableTh label="Total" sortKey="total_cents" sort={invSort.sort} onSort={invSort.toggle} align="right" />
              <SortableTh label="Due" sortKey="due_date" sort={invSort.sort} onSort={invSort.toggle} />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {sortRows(invoices, invSort.sort, (inv, key) => {
              switch (key) {
                case "customer_id":
                  return cName(inv.customer_id).toLowerCase();
                case "period_start":
                  return inv.period_start ? Date.parse(inv.period_start) : 0;
                case "status":
                  return (inv.status || "").toLowerCase();
                case "total_cents":
                  return inv.total_cents ?? 0;
                case "due_date":
                  return inv.due_date ? Date.parse(inv.due_date) : 0;
                default:
                  return (inv.invoice_number || "").toLowerCase();
              }
            }).map((inv) => (
              <tr
                key={inv.id}
                onClick={() => openDetail(inv)}
                className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 text-gray-900 dark:text-gray-100"
              >
                <td className="px-3 py-2 font-mono text-xs">{inv.invoice_number || "DRAFT"}</td>
                <td className="px-3 py-2 text-xs">{cName(inv.customer_id)}</td>
                <td className="px-3 py-2 text-xs">
                  {inv.period_start} → {inv.period_end}
                </td>
                <td className="px-3 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_PILL[inv.status] || STATUS_PILL.draft}`}>
                    {inv.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-mono text-xs">{fmt(inv.total_cents)}</td>
                <td className="px-3 py-2 text-xs">{inv.due_date || "—"}</td>
              </tr>
            ))}
            {invoices.length === 0 && (
              <tr>
                <td className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 italic text-xs" colSpan={6}>
                  No invoices yet — click "Generate invoice" to draft one.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {generating && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-lg w-full mx-4 shadow-xl">
            <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">Generate invoice</h2>
            <div className="space-y-3 text-sm">
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Customer</span>
                <select
                  value={generating.customer_id}
                  onChange={(e) => loadContractsForGenerate(Number(e.target.value))}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                >
                  <option value="">Pick…</option>
                  {customers.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>
              {generating.contracts.length > 0 && (
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Contract</span>
                  <select
                    value={generating.contract_id}
                    onChange={(e) =>
                      setGenerating((g) => g && { ...g, contract_id: Number(e.target.value) })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  >
                    {generating.contracts.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name} ({c.contract_type})
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Period start</span>
                  <input
                    type="date"
                    value={generating.period_start}
                    onChange={(e) =>
                      setGenerating((g) => g && { ...g, period_start: e.target.value })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
                <label className="block">
                  <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Period end</span>
                  <input
                    type="date"
                    value={generating.period_end}
                    onChange={(e) =>
                      setGenerating((g) => g && { ...g, period_end: e.target.value })
                    }
                    className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                  />
                </label>
              </div>
              <label className="block">
                <span className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Message on invoice (optional)</span>
                <textarea
                  value={generating.customer_message}
                  onChange={(e) =>
                    setGenerating((g) => g && { ...g, customer_message: e.target.value })
                  }
                  rows={2}
                  className="w-full border dark:border-gray-600 rounded-lg px-3 py-1.5 bg-white dark:bg-gray-900 dark:text-gray-100"
                />
              </label>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setGenerating(null)} className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200">
                Cancel
              </button>
              <button
                onClick={generate}
                disabled={working || generating.customer_id === ""}
                className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
              >
                {working ? "Generating…" : "Generate draft"}
              </button>
            </div>
          </div>
        </div>
      )}

      {openInvoice && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-3xl w-full mx-4 shadow-xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {openInvoice.invoice_number || "DRAFT invoice"}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {cName(openInvoice.customer_id)} — {openInvoice.period_start} → {openInvoice.period_end}
                </p>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${STATUS_PILL[openInvoice.status]}`}
              >
                {openInvoice.status}
              </span>
            </div>

            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <tr>
                  <SortableTh label="Item" sortKey="description" sort={liSort.sort} onSort={liSort.toggle} />
                  <SortableTh label="Qty" sortKey="quantity_thousandths" sort={liSort.sort} onSort={liSort.toggle} align="right" />
                  <SortableTh label="Rate" sortKey="unit_rate_cents" sort={liSort.sort} onSort={liSort.toggle} align="right" />
                  <SortableTh label="Total" sortKey="total_cents" sort={liSort.sort} onSort={liSort.toggle} align="right" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortRows(openInvoice.line_items, liSort.sort, (li, key) => {
                  switch (key) {
                    case "quantity_thousandths":
                      return li.quantity_thousandths ?? 0;
                    case "unit_rate_cents":
                      return li.unit_rate_cents ?? 0;
                    case "total_cents":
                      return li.total_cents ?? 0;
                    default:
                      return (li.description || "").toLowerCase();
                  }
                }).map((li) => (
                  <tr key={li.id}>
                    <td className="py-2">
                      <div className="font-medium text-sm">{li.description}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400 uppercase">{li.kind}</div>
                    </td>
                    <td className="text-right py-2 font-mono text-xs">
                      {fmtHours(li.quantity_thousandths)} {li.unit_label}
                    </td>
                    <td className="text-right py-2 font-mono text-xs">{fmt(li.unit_rate_cents)}</td>
                    <td className="text-right py-2 font-mono text-sm">{fmt(li.total_cents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="border-t dark:border-gray-700 font-mono text-sm">
                <tr>
                  <td colSpan={3} className="text-right py-2 pr-3">
                    Subtotal
                  </td>
                  <td className="text-right py-2">{fmt(openInvoice.subtotal_cents)}</td>
                </tr>
                <tr>
                  <td colSpan={3} className="text-right py-2 pr-3">
                    Tax
                  </td>
                  <td className="text-right py-2">{fmt(openInvoice.tax_cents)}</td>
                </tr>
                <tr className="font-bold">
                  <td colSpan={3} className="text-right py-2 pr-3">
                    Total
                  </td>
                  <td className="text-right py-2">{fmt(openInvoice.total_cents)}</td>
                </tr>
                {(openInvoice.amount_paid_cents || 0) !== 0 && (
                  <>
                    <tr className="text-green-700 dark:text-green-400">
                      <td colSpan={3} className="text-right py-1 pr-3">
                        Paid
                      </td>
                      <td className="text-right py-1">-{fmt(openInvoice.amount_paid_cents)}</td>
                    </tr>
                    <tr className="font-bold">
                      <td colSpan={3} className="text-right py-1 pr-3">
                        Balance
                      </td>
                      <td className="text-right py-1">{fmt(openInvoice.balance_cents)}</td>
                    </tr>
                  </>
                )}
              </tfoot>
            </table>

            {/* Payments — only meaningful once the invoice is finalized. */}
            {openInvoice.status !== "draft" && openInvoice.status !== "void" && (
              <div className="mt-5 border-t dark:border-gray-700 pt-4">
                <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">Payments</h3>
                {payments.length > 0 && (
                  <table className="w-full text-xs mb-3">
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                      {payments.map((p) => (
                        <tr key={p.id}>
                          <td className="py-1 text-gray-500 dark:text-gray-400">{p.received_at}</td>
                          <td className="py-1 uppercase text-gray-500 dark:text-gray-400">{p.method}</td>
                          <td className="py-1 text-gray-500 dark:text-gray-400">{p.reference || ""}</td>
                          <td className="py-1 text-right font-mono">{fmt(p.amount_cents)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                {openInvoice.balance_cents > 0 && (
                  <div className="flex items-end gap-2">
                    <label className="text-xs flex-1">
                      <span className="block text-gray-500 dark:text-gray-400 mb-1">Amount</span>
                      <input
                        type="number"
                        step="0.01"
                        value={payForm.amount}
                        onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                        placeholder={(openInvoice.balance_cents / 100).toFixed(2)}
                        className="w-full border dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100"
                      />
                    </label>
                    <label className="text-xs">
                      <span className="block text-gray-500 dark:text-gray-400 mb-1">Method</span>
                      <select
                        value={payForm.method}
                        onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                        className="border dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100"
                      >
                        {["check", "ach", "card", "cash", "wire", "other"].map((m) => (
                          <option key={m} value={m}>
                            {m}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-xs flex-1">
                      <span className="block text-gray-500 dark:text-gray-400 mb-1">Reference</span>
                      <input
                        value={payForm.reference}
                        onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })}
                        placeholder="check # / txn id"
                        className="w-full border dark:border-gray-600 rounded-lg px-2 py-1 bg-white dark:bg-gray-900 dark:text-gray-100"
                      />
                    </label>
                    <button
                      onClick={recordPayment}
                      disabled={working}
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg disabled:opacity-50"
                    >
                      Record
                    </button>
                  </div>
                )}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2 flex-wrap">
              <button onClick={() => setOpenInvoice(null)} className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200">
                Close
              </button>
              <ActionButton onAction={() => downloadPdf()} variant="raw" className="px-4 py-2 text-sm border rounded-lg text-gray-700 dark:text-gray-200" pendingLabel="Preparing…">
                Download PDF
              </ActionButton>
              {openInvoice.status === "draft" && (
                <>
                  <button
                    onClick={voidInv}
                    disabled={working}
                    className="px-4 py-2 text-sm text-red-700 dark:text-red-400 border border-red-300 dark:border-red-800 rounded-lg disabled:opacity-50"
                  >
                    Void
                  </button>
                  <button
                    onClick={finalize}
                    disabled={working}
                    className="px-4 py-2 text-sm border dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 disabled:opacity-50"
                  >
                    Finalize
                  </button>
                </>
              )}
              {openInvoice.status !== "void" && (
                <button
                  onClick={emailInvoice}
                  disabled={working}
                  className="px-4 py-2 text-sm bg-brand text-white rounded-lg disabled:opacity-50"
                >
                  {openInvoice.status === "draft" ? "Finalize & email" : "Email invoice"}
                </button>
              )}
              {openInvoice.status === "sent" && (
                <button
                  onClick={markPaid}
                  disabled={working}
                  className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg disabled:opacity-50"
                >
                  Mark fully paid
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
